<template>
  <label
    class="checkbox"
    :class="[
      {
        'checkbox--checked': mounted && model,
        'checkbox--disabled': disabled,
      },
      size ? `checkbox--${size}` : '',
      color ? `checkbox--color-${color}` : '',
    ]"
    tabindex="0"
    :aria-disabled="disabled"
    :aria-checked="!!model"
    role="checkbox"
    @keydown.enter.prevent="onKeyboardEnter"
    @keydown.space.prevent="onKeyboardEnter"
  >
    <input
      v-model="model"
      v-bind="bindings"
      type="checkbox"
      data-hj-suppress
      @input="onInput"
      @update:value="onUpdateValue"
    />
    <span
      v-if="!labelIsHtml"
      class="checkbox-label"
    >
      {{ label }}
      <span
        v-if="required"
        class="mandatory"
        >*</span
      >
      <span
        v-if="$slots.afterLabel"
        class="checkbox-label-after"
      >
        <slot name="afterLabel" />
      </span>
    </span>
    <span
      v-else
      class="checkbox-label checkbox-label--secondary"
      v-html="label"
    />
  </label>
</template>

<script setup lang="ts">
import { defineInputBindings } from '@/mixins/InputFieldAttributes';
import type { InputFieldProps } from '@/mixins/InputFieldAttributes';

const props = withDefaults(
  defineProps<
    InputFieldProps & {
      label: string;
      labelIsHtml?: boolean;
      size?: string;
      color?: string;
    }
  >(),
  {
    label: '',
    size: '',
    color: '',
  },
);

const emit = defineEmits(['input', 'update:value']);
const model = defineModel<string | boolean>('value');
const mounted = ref(false);
const bindings = defineInputBindings(props);

const onInput = (event: InputEvent) => {
  emit('input', event?.target?.checked);
};

const onKeyboardEnter = () => {
  emit('input', !model.value);
  emit('update:value', !model.value);
};

const onUpdateValue = (event: InputEvent) => {
  emit('update:value', event?.target?.value);
};

onMounted(() => {
  mounted.value = true;
});
</script>

<style lang="scss" scoped>
.checkbox {
  position: relative;
  display: flex;
  cursor: pointer;

  // Checkbox
  &:before {
    content: '';
    position: relative;
    display: inline-block;
    width: 1.5 * $pad;
    height: 1.5 * $pad;
    background-color: $color-general-background-neutral-default;
    border: 1px solid palette(grey, metallic);
    border-radius: map-get($border-radiuses, 'l');
    flex-shrink: 0;
    margin-top: 2px;
  }

  // Checked indicator
  &:after {
    content: '';
    position: absolute;
    display: inline-block;
    width: 11px;
    height: 19px;
    top: 0;
    left: 11px;
    margin: auto 0;
    z-index: 2;
    border-bottom: 4px solid palette(orientation, green);
    border-right: 4px solid palette(orientation, green);
    transform: rotate(45deg);
    transition: all 0.1s ease-in-out;
    opacity: 0;
  }

  &:focus-visible {
    outline: none;
    &:before {
      @include focus-outline-default;
    }
  }

  // Colors
  @each $palette, $tones in $palettes {
    @each $tone, $hex in $tones {
      @if $tone == 'base' {
        &.checkbox--color-#{$palette} {
          &:after {
            border-bottom-color: $hex;
            border-right-color: $hex;
          }
        }
      } @else {
        &.checkbox--color-#{$palette}-#{$tone} {
          &:after {
            border-bottom-color: $hex;
            border-right-color: $hex;
          }
        }
      }
    }
  }

  input {
    display: none;
  }

  .checkbox-label {
    position: relative;
    top: 3px;
    margin-left: math.div($pad, 1.8);
    font-size: map-get($font-sizes, 'l');
    flex: 1 1 auto;

    .mandatory {
      margin-right: $pad;
    }
  }

  .checkbox-label-after {
    margin-left: $pad * 0.5;
  }

  &.checkbox--checked {
    &:after {
      opacity: 1;
    }
  }

  &.checkbox--disabled {
    opacity: 0.65;
    pointer-events: none;

    &:before {
      background-color: palette(grey, light);
    }
  }

  &.checkbox--l {
    &:before {
      width: $space;
      height: $space;
    }
    &:after {
      width: 12px;
      height: 20px;
      border-bottom-width: 4px;
      border-right-width: 4px;
      left: 9px;
      top: -2px;
    }
  }
}

.checkbox-label--secondary {
  :deep(a) {
    color: $color-brand-fnmz-green;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
}
</style>
